import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Children, cloneElement, isValidElement, useRef } from "react";

// Curva "luxury standard" (invisible motion, biblia Kelo)
const EASE = [0.22, 1, 0.36, 1];
const EASE_LONG = [0.16, 1, 0.3, 1];

// -----------------------------------------------------------------------------
// Reveal — fade + subida sutil al entrar al viewport (sin rebote, sin overshoot)
// -----------------------------------------------------------------------------
export const Reveal = ({ children, delay = 0, y = 40, duration = 0.8, className, ...rest }) => {
  const reduce = useReducedMotion();
  const initial = reduce ? { opacity: 0 } : { opacity: 0, y };
  const animate = reduce ? { opacity: 1 } : { opacity: 1, y: 0 };
  return (
    <motion.div
      initial={initial}
      whileInView={animate}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: reduce ? 0.3 : duration, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};

// -----------------------------------------------------------------------------
// Stagger — orquesta la aparición de N hijos con retardo progresivo
// Cada hijo debe ser un elemento válido; se le añade animación en cadena.
// -----------------------------------------------------------------------------
export const Stagger = ({ children, delay = 0, step = 0.08, y = 24, className }) => {
  const reduce = useReducedMotion();
  const items = Children.toArray(children).filter(isValidElement);
  return (
    <div className={className}>
      {items.map((child, i) =>
        cloneElement(child, {
          key: child.key ?? i,
          style: {
            ...(child.props.style || {}),
          },
          children: (
            <motion.span
              style={{ display: "contents" }}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
              whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: reduce ? 0.3 : 0.7, delay: delay + i * step, ease: EASE }}
            >
              {child.props.children}
            </motion.span>
          ),
        })
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// StaggerGrid — versión que envuelve cada hijo en su propio motion.div
// (útil cuando los hijos son <div>, <li>, <a>, etc. con estructura propia)
// -----------------------------------------------------------------------------
export const StaggerGrid = ({ children, delay = 0, step = 0.08, y = 24, className }) => {
  const reduce = useReducedMotion();
  const items = Children.toArray(children);
  return (
    <div className={className}>
      {items.map((child, i) => (
        <motion.div
          key={i}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y }}
          whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: reduce ? 0.3 : 0.7, delay: delay + i * step, ease: EASE }}
          style={{ display: "contents" }}
        >
          {child}
        </motion.div>
      ))}
    </div>
  );
};

// -----------------------------------------------------------------------------
// MaskedLine — línea con máscara y subida (usada en titulares tipo Cartier)
// -----------------------------------------------------------------------------
export const MaskedLine = ({ children, delay = 0, className }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={className}
        style={{ display: "block" }}
        initial={reduce ? { opacity: 0 } : { y: "110%" }}
        animate={reduce ? { opacity: 1 } : { y: "0%" }}
        transition={{ duration: reduce ? 0.3 : 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

// -----------------------------------------------------------------------------
// ImageReveal — revelado tipo cortina + zoom out sutil (para hero/imágenes)
// -----------------------------------------------------------------------------
export const ImageReveal = ({ children, className, delay = 0 }) => {
  const reduce = useReducedMotion();
  return (
    <div className={`relative overflow-hidden ${className || ""}`}>
      <motion.div
        initial={reduce ? { opacity: 0 } : { scale: 1.12, opacity: 0 }}
        whileInView={reduce ? { opacity: 1 } : { scale: 1, opacity: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: reduce ? 0.3 : 1.4, delay, ease: EASE_LONG }}
        className="h-full w-full"
      >
        {children}
      </motion.div>
      {/* cortina de máscara — pasa de derecha a izquierda */}
      {!reduce && (
        <motion.div
          initial={{ scaleX: 1 }}
          whileInView={{ scaleX: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 1.1, delay: delay + 0.05, ease: EASE_LONG }}
          style={{ transformOrigin: "right" }}
          className="pointer-events-none absolute inset-0 bg-[#f7f7f5]"
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Parallax — traslación vertical basada en scroll (muy sutil, para imágenes)
// intensity: 0..1 (recomendado 0.15 - 0.3)
// -----------------------------------------------------------------------------
export const Parallax = ({ children, intensity = 0.2, className }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80 * intensity, -80 * intensity]);
  return (
    <div ref={ref} className={`overflow-hidden ${className || ""}`}>
      <motion.div style={reduce ? undefined : { y }} className="h-full w-full">
        {children}
      </motion.div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// SoftFade — fade con desplazamiento mínimo (para bloques pequeños de texto)
// -----------------------------------------------------------------------------
export const SoftFade = ({ children, delay = 0, className, ...rest }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
      whileInView={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: reduce ? 0.25 : 0.6, delay, ease: EASE }}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
};
